import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../context/EditSessionsContext";
import { Button, Heading, Text } from "@foris/avocado-suite";
import { ContextApp, IContextApp } from "@config/Context/contextApp";
import { ContextEDH } from "@context/ContextEDH";
import { Types } from "../../context/formData.reducer";
import { useHistory } from "react-router-dom";
import { IWeek } from "@modules/sections/ISections";
import { allSessionsCanBeSaved, sessionIsSavable } from "../../utils/context";
import { isSessionWeekOutsideThreshold } from "../../utils/isOutsideThreshold";
import { needsPastFork } from "../../utils/needsPastFork";
import { useForkOrchestration } from "../../hooks/useForkOrchestration";
import Modal from "@components/modal";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import styles from "./assignationHeader.module.scss";
import { useTranslation } from "react-i18next";

interface AssignationHeaderProps {
  locationCurrentSelectedWeek?: IWeek;
  onSubmitValidations?: () => void;
  onShowValidationWarnings?: (showWarnings: boolean) => void;
}

const AssignationHeader = ({
  locationCurrentSelectedWeek,
  onSubmitValidations,
  onShowValidationWarnings,
}: AssignationHeaderProps) => {
  const history = useHistory();
  const { id } = useContextUrl();
  const { editorUrl } = useNavigationUrl();
  const { state, dispatch } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const context: IContextApp = useContext(ContextApp);
  const { runFork } = useForkOrchestration();
  const [showPastForkModal, setShowPastForkModal] = useState(false);
  const [isSubmitPendingAfterFork, setIsSubmitPendingAfterFork] = useState(false);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const thresholdDate = state?.form?.blockingThreshold ?? null;
  const canEditPastSessions = !!stateEDH?.base?.base?.user?.abilities?.can_edit_past_sessions;

  const sessionsToFork = useMemo(
    () => needsPastFork(state?.form, state?.link, thresholdDate, canEditPastSessions),
    [state?.form, state?.link, thresholdDate, canEditPastSessions],
  );

  const selectedSessionsLabel = useMemo(() => {
    const selectedSessionsLength = state?.form?.selectedSessions?.length ?? 0;

    if (selectedSessionsLength === 1) {
      return t("assignation-edit.header.singular-selected-sessions");
    }

    return t("assignation-edit.header.selected-sessions", {
      count: selectedSessionsLength,
    });
  }, [state?.form?.selectedSessions, language]);

  const userCanEditOrDeleteSessions = context?.user?.abilities?.can_edit_or_delete_sessions;
  const numberOfSavedSessions = state?.form?.savedSessions?.length ?? 0;
  const numberOfSessionsToCreate = state?.form?.sessionsToCreate?.length ?? 0;

  const numberOfDeletedSessions = useMemo(() => {
    return Object.keys(state?.form?.sessionsToDelete ?? {}).length;
  }, [state?.form?.sessionsToDelete]);

  const someCreatedSessionIsSavable = useMemo(() => {
    const anyCreatedSessionIsSavable = (state?.form?.sessionsToCreate ?? []).some(sessionIsSavable);
    const someSessionHasBeenSaved = Boolean(numberOfSavedSessions);
    const someSessionHasBeenDeleted = Boolean(numberOfDeletedSessions);
    return someSessionHasBeenSaved || someSessionHasBeenDeleted || anyCreatedSessionIsSavable;
  }, [state?.form?.savedSessions, state?.form?.sessionsToCreate]);

  const allSavedSessionsAreSavable = useMemo(
    () => (state?.form?.savedSessions ?? []).every(sessionIsSavable),
    [state?.form?.savedSessions],
  );

  const allSessionsAreDeleted = useMemo(
    () =>
      (state?.form?.selectedSessions ?? []).every(
        session => state?.form?.sessionsToDelete?.[session.id ?? "-"],
      ),
    [state?.form?.selectedSessions, state?.form?.sessionsToDelete],
  );

  const someSelectedSessionHasBeenDeleted = useMemo(
    () =>
      (state?.form?.selectedSessions ?? []).some(
        session => state?.form?.sessionsToDelete?.[session.id ?? "-"],
      ),
    [state?.form?.selectedSessions, state?.form?.sessionsToDelete],
  );

  const proceedWithValidation = () => {
    if (!numberOfSavedSessions && !numberOfSessionsToCreate && !numberOfDeletedSessions) {
      return;
    }

    if (
      allSessionsCanBeSaved(state?.form?.sessionsToCreate) &&
      !state?.form?.selectedCreateSession
    ) {
      onSubmitValidations?.();
      return;
    }

    onShowValidationWarnings?.(true);
  };

  const handleSubmitValidation = () => {
    if (sessionsToFork.length > 0) {
      setShowPastForkModal(true);
      return;
    }

    proceedWithValidation();
  };

  const handlePastForkConfirm = () => {
    setShowPastForkModal(false);
    runFork(
      (week, session) => isSessionWeekOutsideThreshold(thresholdDate, week, session),
      "assignation-edit.past-sessions.fork-toast",
      sessionsToFork,
    );
    setIsSubmitPendingAfterFork(true);
  };

  // Invariant: `isSubmitPendingAfterFork` lives for exactly one render cycle.
  // It is raised right after the fork dispatch (`ForkSelectedSessions`) and
  // consumed here on the next render, once the
  // reducer has committed the new state. We need this defer because
  // `proceedWithValidation` closes over `state?.form?.{savedSessions,
  // sessionsToCreate,...}` — calling it synchronously would validate against
  // the pre-fork snapshot. Do NOT call `setIsSubmitPendingAfterFork(true)`
  // from anywhere else: a duplicate raise would silently trigger a second
  // submit.
  useEffect(() => {
    if (!isSubmitPendingAfterFork) return;
    setIsSubmitPendingAfterFork(false);
    proceedWithValidation();
  }, [isSubmitPendingAfterFork]);

  const handlePastForkCancel = () => {
    setShowPastForkModal(false);
  };

  return (
    <header className={styles.assignationHeader}>
      <div className={styles.assignationHeader_leftContent}>
        <Heading type="h3">{selectedSessionsLabel}</Heading>

        <div className={styles.actions}>
          {userCanEditOrDeleteSessions && (
            <Button
              size="sm"
              leftIcon="trash"
              variant="ghost"
              disabled={allSessionsAreDeleted}
              onClick={() => {
                dispatch({
                  type: Types.RemoveSelectedSessions,
                  payload: state?.form?.selectedSessions,
                });
              }}
            >
              {t("assignation-edit.header.delete")}
            </Button>
          )}

          <Button
            size="sm"
            leftIcon="repeat"
            variant="ghost"
            disabled={
              !state?.form?.selectedSessions?.length ||
              (!numberOfSavedSessions &&
                !numberOfSessionsToCreate &&
                !someSelectedSessionHasBeenDeleted)
            }
            onClick={() => {
              dispatch({
                type: Types.UndoEditionOverSelectedSessions,
                payload: state?.form?.selectedSessions,
              });
            }}
          >
            {t("assignation-edit.header.restore-changes")}
          </Button>

          {userCanEditOrDeleteSessions && (
            <Button
              size="sm"
              leftIcon="copy"
              variant="ghost"
              disabled={!state?.form?.selectedSessions?.length}
              onClick={() => {
                dispatch({ type: Types.AddSessionsToCreate, payload: "cloned" });
              }}
            >
              {t("assignation-edit.header.duplicate")}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.assignationHeader_rightContent}>
        <Button
          size="md"
          variant="ghost"
          onClick={() => {
            history.push(editorUrl("link", id), {
              currentSelectedWeek: locationCurrentSelectedWeek,
              sessionId: undefined,
            });
          }}
        >
          {t("assignation-edit.header.go-to-league")}
        </Button>
        <Button
          size="md"
          variant="primary"
          disabled={
            (numberOfSavedSessions === 0 &&
              numberOfSessionsToCreate === 0 &&
              numberOfDeletedSessions === 0) ||
            !someCreatedSessionIsSavable ||
            !allSavedSessionsAreSavable
          }
          onClick={handleSubmitValidation}
        >
          {t("assignation-edit.header.validate")}
        </Button>
      </div>

      <Modal
        typeState="confirm"
        title={t("assignation-edit.past-sessions.fork-modal.title")}
        show={showPastForkModal}
        textButtonPrincipal={t("assignation-edit.past-sessions.fork-modal.confirm")}
        textButtonSecondary={t("assignation-edit.past-sessions.fork-modal.cancel")}
        onClickPrincipal={handlePastForkConfirm}
        onClickSecondary={handlePastForkCancel}
        onClose={handlePastForkCancel}
      >
        <Text>{t("assignation-edit.past-sessions.fork-modal.body.main")}</Text>
        <ul className={styles.forkList}>
          <li className={styles.forkList_item}>
            <Text weight="medium" className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.original-sessions.title")}:
            </Text>{" "}
            <Text className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.original-sessions.description")}
            </Text>
          </li>
          <li className={styles.forkList_item}>
            <Text weight="medium" className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.new-sessions.title")}:
            </Text>{" "}
            <Text className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.new-sessions.description")}
            </Text>
          </li>
        </ul>
      </Modal>
    </header>
  );
};

export default AssignationHeader;
